import { skipToken } from '@reduxjs/toolkit/query';
import { addDays, format, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { dayKeyFromIso } from './calendarGrid';
import { buildNativeAppointmentGoogleEventIds } from './nativeAppointmentEventId';
import {
  buildAppointmentsByUuid,
  isMatchedNativeAppointmentPlaceholder,
} from './nativeAppointmentPlaceholder';
import { flattenSessionGroups } from './sessionData';

import type { AppointmentActionRecord } from '@/features/appointments/appointmentActionRecord';
import type { Session } from '@/types';
import type {
  ExternalCalendarAvailabilityWindow,
  ExternalCalendarEvent,
} from '@/types/calendar';

import {
  actionRecordEndAt,
  actionRecordStartAt,
  reconcileCalendarAppointmentActionRecords,
} from '@/features/appointments/appointmentActionRecord';
import { platformAdminCalendarAppointmentRecord } from '@/features/appointments/calendarAppointmentProjection';
import { legacyAppSelectedPair } from '@/features/appointments/momentsSelectedPairContract';
import { useGetExternalEventsQuery } from '@/store/api/calendarApi';
import {
  useGetMomentsCoachWideAppointmentsQuery,
  useGetMomentsSelectedPairAppointmentsQuery,
} from '@/store/api/momentsAppointmentApi';
import {
  useGetSessionsByCoachQuery,
  useLazyGetSessionsByCoachQuery,
} from '@/store/api/sessionApi';

const iso = (date: Date) => format(date, 'yyyy-MM-dd');

const eventTouchesDays = (
  event: ExternalCalendarEvent,
  startKey: string,
  endKey: string,
  timeZone?: string | null,
): boolean => {
  if (event.allDay) return event.start <= endKey && event.end >= startKey;
  try {
    return (
      dayKeyFromIso(event.start, timeZone) <= endKey &&
      dayKeyFromIso(
        new Date(Date.parse(event.end) - 1).toISOString(),
        timeZone,
      ) >= startKey
    );
  } catch {
    // Leave malformed events to the grid, which already skips them.
    return true;
  }
};
const SESSION_PAGE_SIZE = 200;
export const ADMIN_NATIVE_LOAD_ERROR_MESSAGE =
  'Unable to load selected Coach appointments.';
export const PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE =
  'Unable to load appointments. Please try again.';

export const viewerDayStartIso = (
  dateKey: string,
  timeZone?: string | null,
): string => {
  if (!timeZone) return `${dateKey}T00:00:00.000Z`;
  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  const target = Date.UTC(year, month - 1, day);
  const formatter = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  });
  let instant = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = formatter.formatToParts(new Date(instant));
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);
    const represented = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute'),
      value('second'),
    );
    instant += target - represented;
  }
  return new Date(instant).toISOString();
};

export const platformAdminAppointmentWindow = (
  timeZone?: string | null,
): { rangeEnd: string; rangeStart: string } => {
  const todayKey = dayKeyFromIso(new Date().toISOString(), timeZone);
  const todayMidday = new Date(`${todayKey}T12:00:00Z`);
  return {
    rangeEnd: viewerDayStartIso(
      format(addDays(todayMidday, 370), 'yyyy-MM-dd'),
      timeZone,
    ),
    rangeStart: viewerDayStartIso(
      format(subDays(todayMidday, 30), 'yyyy-MM-dd'),
      timeZone,
    ),
  };
};

export const exactVisibleWindow = (
  rangeStart: Date,
  rangeEnd: Date,
  timeZone?: string | null,
): { rangeEnd: string; rangeStart: string } => {
  const startKey = dayKeyFromIso(rangeStart.toISOString(), timeZone);
  const inclusiveEndKey = dayKeyFromIso(rangeEnd.toISOString(), timeZone);
  const endKey = format(
    addDays(new Date(`${inclusiveEndKey}T12:00:00Z`), 1),
    'yyyy-MM-dd',
  );
  return {
    rangeEnd: viewerDayStartIso(endKey, timeZone),
    rangeStart: viewerDayStartIso(startKey, timeZone),
  };
};

// Fetches the coaching sessions and the coach's external calendar events that
// fall within [rangeStart, rangeEnd] — shared by the Week and Month views.
export const useCalendarWindow = (
  coachId: string,
  rangeStart: Date,
  rangeEnd: Date,
  selectedMemberId: string | null,
  viewerTimezone?: string | null,
  adminCoachId?: string,
): {
  sessions: Session[];
  // MEN-4695: reconciled view of native Appointments merged with legacy
  // Sessions — consumers rewiring to the clickable-card path use this;
  // legacy consumers keep reading `sessions` until they migrate.
  actionRecords: AppointmentActionRecord[];
  events: ExternalCalendarEvent[];
  availabilityWindows?: ExternalCalendarAvailabilityWindow[] | null;
  chemistryAvailabilityWindows?: ExternalCalendarAvailabilityWindow[] | null;
  timeZone?: string | null;
  adminNativeLoadError: boolean;
  participantNativeLoadError: boolean;
  isLoading: boolean;
} => {
  const start = iso(rangeStart);
  const end = iso(rangeEnd);
  const sessionStart = iso(subDays(rangeStart, 1));
  // MEN-5421: Rails compares `started_at <= maxDate` at UTC midnight, which
  // would cut the last visible day at its very start; the viewer's last day
  // can run until noon UTC the day after. Records are clipped to the visible
  // range below.
  const sessionEnd = iso(addDays(rangeEnd, 2));

  const baseArgs = useMemo(
    () => ({
      coachId,
      limit: SESSION_PAGE_SIZE,
      maxDate: sessionEnd,
      minDate: sessionStart,
      order: 'asc' as const,
      ...(selectedMemberId ? { userId: selectedMemberId } : {}),
    }),
    [coachId, selectedMemberId, sessionEnd, sessionStart],
  );
  const firstPageQuery = useGetSessionsByCoachQuery({ ...baseArgs, page: 1 });
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
  const [loadSessions, extraPagesQuery] = useLazyGetSessionsByCoachQuery();

  // MEN-5421: Moments cuts the date range at the coach's midnight, but the Ops
  // grid renders in the viewer's zone. Pad a day on each side so the edge days
  // stay whole; events outside the visible days are dropped below.
  const eventsQuery = useGetExternalEventsQuery({
    coachId: adminCoachId,
    end: adminCoachId ? iso(addDays(rangeEnd, 1)) : end,
    start: adminCoachId ? iso(subDays(rangeStart, 1)) : start,
    viewerTimezone,
  });

  useEffect(() => {
    let active = true;
    const firstPage = firstPageQuery.currentData;
    if (!firstPage) {
      setSessions([]);
      setIsLoadingMoreSessions(false);
      return () => {
        active = false;
      };
    }

    const fetchPages = async () => {
      const allSessions = [...flattenSessionGroups(firstPage.items)];
      if (active) setSessions(allSessions);

      let page = 2;
      const pages = firstPage.pages || 1;
      if (pages < 2) {
        setIsLoadingMoreSessions(false);
        return;
      }

      setIsLoadingMoreSessions(true);

      try {
        while (page <= pages) {
          // eslint-disable-next-line no-await-in-loop -- the first response tells us the page count.
          const data = await loadSessions({ ...baseArgs, page }).unwrap();
          allSessions.push(...flattenSessionGroups(data.items));
          page += 1;
        }

        if (active) setSessions(allSessions);
      } catch {
        if (active) setSessions(allSessions);
      } finally {
        if (active) setIsLoadingMoreSessions(false);
      }
    };

    fetchPages();
    return () => {
      active = false;
    };
  }, [firstPageQuery.currentData, loadSessions, baseArgs]);

  const eventsData = eventsQuery.currentData;

  // Fetch native Appointments once for both the MEN-4665 / MEN-4695
  // dedup filters and the MEN-4688 title rewrite. RTK Query caches
  // across all three consumers so this is a single request.
  //
  // MEN-4695: scope the appointments query the same way sessions are
  // scoped — passing `memberId: selectedMemberId` when in member-focused
  // view so the reconciler doesn't pick up unrelated members.
  //
  // These reads use the direct Moments platform browser APIs for both the
  // signed-in coach schedule and selected-coach admin schedule. Do not fall
  // back to the Rails `/api/v1/appointments` proxy here: backend deployments
  // can remove or freeze that proxy independently of this frontend.
  const appointmentCoachId = adminCoachId ?? coachId;
  const appointmentWindow = useMemo(
    () => exactVisibleWindow(rangeStart, rangeEnd, viewerTimezone),
    [rangeEnd, rangeStart, viewerTimezone],
  );
  const selectedPairAppointmentsQuery =
    useGetMomentsSelectedPairAppointmentsQuery(
      selectedMemberId
        ? {
            ...legacyAppSelectedPair(appointmentCoachId, selectedMemberId),
            limit: SESSION_PAGE_SIZE,
            rangeEnd: appointmentWindow.rangeEnd,
            rangeStart: appointmentWindow.rangeStart,
          }
        : skipToken,
    );
  const coachWideAppointmentsQuery = useGetMomentsCoachWideAppointmentsQuery(
    !selectedMemberId
      ? {
          coach: { externalId: appointmentCoachId, provider: 'legacy_app' },
          limit: SESSION_PAGE_SIZE,
          ...appointmentWindow,
        }
      : skipToken,
  );
  const selectedPairAppointmentsPending = Boolean(
    selectedMemberId &&
    !selectedPairAppointmentsQuery.currentData &&
    (selectedPairAppointmentsQuery.isLoading ||
      selectedPairAppointmentsQuery.isFetching),
  );
  const activeAppointmentQuery = selectedMemberId
    ? selectedPairAppointmentsQuery
    : coachWideAppointmentsQuery;
  const adminNativeLoadError = Boolean(
    adminCoachId && activeAppointmentQuery.isError,
  );
  const participantNativeLoadError = Boolean(
    !adminCoachId && activeAppointmentQuery.isError,
  );
  const appointmentRecords = useMemo(
    () =>
      (activeAppointmentQuery.currentData?.appointments ?? []).map(
        platformAdminCalendarAppointmentRecord,
      ),
    [activeAppointmentQuery.currentData?.appointments],
  );
  const effectiveProjections = useMemo(
    () => appointmentRecords.map((record) => record.view),
    [appointmentRecords],
  );

  // MEN-4665: dedup the external Google Calendar events feed against the
  // native Mento sessions. When SyncGoogleCalendarAndSessionsJob writes a
  // Session's `event_id`, that value is the id of the paired Google event
  // — the same id that ExternalCalendarEvent.id carries when the events
  // feed returns it. Without this filter, WeekView renders the session
  // once as a green sage block AND once as a yellow "Google (Member)
  // Validation" event on the same slot, because the two lists arrive
  // through separate feeds (`useGetSessionsByCoachQuery` for sessions,
  // `useGetExternalEventsQuery` for the raw calendar). The dedup is safe:
  // sessions with a null event_id have no paired projection yet, so
  // filtering nothing is a no-op.
  const syncedGoogleEventIds = useMemo(() => {
    const ids = new Set<string>();
    sessions.forEach((session) => {
      if (session.eventId) ids.add(session.eventId);
    });
    return ids;
  }, [sessions]);

  // Build a UUID map only to recognize historical placeholder summaries after
  // authoritative event-id reconciliation. Appointment UUIDs are never event identity.
  const nativeAppointmentsByUuid = useMemo(
    () => buildAppointmentsByUuid(effectiveProjections),
    [effectiveProjections],
  );

  const nativeGoogleEventIds = useMemo(
    () => buildNativeAppointmentGoogleEventIds(effectiveProjections),
    [effectiveProjections],
  );

  const filteredEvents = useMemo(() => {
    const items = adminCoachId
      ? (eventsData?.items ?? []).filter((event) =>
          eventTouchesDays(event, start, end, viewerTimezone),
        )
      : (eventsData?.items ?? []);
    if (
      syncedGoogleEventIds.size === 0 &&
      nativeGoogleEventIds.size === 0 &&
      nativeAppointmentsByUuid.size === 0
    ) {
      return items;
    }
    return items.filter((event) => {
      if (syncedGoogleEventIds.has(event.id)) return false;
      if (nativeGoogleEventIds.has(event.id)) return false;
      if (
        isMatchedNativeAppointmentPlaceholder(event, nativeAppointmentsByUuid)
      ) {
        return false;
      }
      return true;
    });
  }, [
    adminCoachId,
    end,
    eventsData?.items,
    start,
    viewerTimezone,
    syncedGoogleEventIds,
    nativeGoogleEventIds,
    nativeAppointmentsByUuid,
  ]);

  // MEN-4695: merge the two authoritative feeds (native Appointments +
  // legacy Sessions) into a single time-ordered list where every entry
  // exposes the same accessor surface via the AppointmentActionRecord
  // discriminated union. Legacy Sessions that pair with a native
  // Appointment are collapsed onto the native record (the reconciler
  // matches by sourceAppointmentId / momentsBookingId); Sessions without
  // a paired Appointment pass through unchanged. Downstream views
  // migrate onto this list in slice 3+.
  //
  // Filter appointments to those overlapping with [rangeStart, rangeEnd]
  // so actionRecords stays scoped to the visible calendar window, matching
  // the sessions query which uses minDate/maxDate.

  const actionRecords = useMemo(() => {
    if (adminNativeLoadError || selectedPairAppointmentsPending) return [];
    const records = reconcileCalendarAppointmentActionRecords(
      appointmentRecords,
      sessions,
    );
    const rangeStartKey = iso(rangeStart);
    const rangeEndKey = iso(rangeEnd);
    return records.filter((record) => {
      const recordStart = actionRecordStartAt(record);
      const recordEnd = actionRecordEndAt(record);
      if (
        Number.isNaN(Date.parse(recordStart)) ||
        Number.isNaN(Date.parse(recordEnd))
      ) {
        return false;
      }
      return (
        dayKeyFromIso(recordStart, viewerTimezone) <= rangeEndKey &&
        dayKeyFromIso(
          new Date(Date.parse(recordEnd) - 1).toISOString(),
          viewerTimezone,
        ) >= rangeStartKey
      );
    });
  }, [
    adminNativeLoadError,
    selectedPairAppointmentsPending,
    appointmentRecords,
    rangeEnd,
    rangeStart,
    sessions,
    viewerTimezone,
  ]);

  return {
    actionRecords,
    adminNativeLoadError,
    availabilityWindows: eventsData?.availabilityWindows,
    chemistryAvailabilityWindows: eventsData?.chemistryAvailabilityWindows,
    events: filteredEvents,
    isLoading:
      (!firstPageQuery.currentData &&
        (firstPageQuery.isLoading || firstPageQuery.isFetching)) ||
      (!eventsData && eventsQuery.isFetching) ||
      (sessions.length === 0 &&
        (isLoadingMoreSessions || extraPagesQuery.isFetching)) ||
      // Wait for native appointments before rendering so matched historical
      // placeholder events never flash beside their native Appointment card.
      activeAppointmentQuery.isLoading ||
      selectedPairAppointmentsPending,
    participantNativeLoadError,
    sessions,
    timeZone: eventsData?.timeZone,
  };
};
